import { LOCALIZED_FIELDS, type LocalizedCopy } from './ad-language';
import { adApiRequest, type AdApiResult, type AdSubject, type AdSubjectType } from './banner-kit-api';
import { fetchLandingLegal, siteLanguageList } from './landing-page-api';

/**
 * The ad language's two reads: the brand website's languages (the Language picker's options and the default a new
 * asset starts in) and the advertised subject's copy in one language (`ad-copy-localize`, a free synchronous op of
 * spark-creatives: the promotion record's own text in that language, else Lumio's faithful translation).
 */

/** The `/creatives/generate` media type (and constant prompt) of the copy localization op. */
const LOCALIZE_MEDIA_TYPE = 'ad-copy-localize';
const LOCALIZE_PROMPT = 'ad copy localize';

const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** What {@link localizeAdCopy} localizes: the subject as the asset snapshotted it, in `language`. */
export interface LocalizeAdCopyRequest {
  language: string;
  subjectType: AdSubjectType;
  subject: Pick<AdSubject, 'id' | 'name' | 'headline' | 'subtitle' | 'cta_text'>;
}

/** The answer of `ad-copy-localize` (raw JSON; `ok: false` carries a short reason). */
interface LocalizeAnswer {
  ok?: boolean;
  error?: unknown;
  language?: unknown;
  headline?: unknown;
  subtitle?: unknown;
  cta_text?: unknown;
  sources?: unknown;
}

/**
 * The subject's copy in `language` (free, synchronous). A promotion reads its live record's own text in that
 * language and translates only what the record lacks; a game or feature has only its subtitle and CTA translated.
 * `translated` names the fields Lumio translated. A refusal, an unreadable record or a failed translation is a
 * failure result (`status` 200 when the service answered `ok: false`); never throws.
 */
export const localizeAdCopy = async (
  tenantId: number,
  { language, subjectType, subject }: LocalizeAdCopyRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<LocalizedCopy>> => {
  // The same envelope as banner-kit-api's adSyncBody (a synchronous ad op on the generate route).
  const body = {
    tenant_id: String(tenantId),
    placement: 'banner_kit',
    prompt: LOCALIZE_PROMPT,
    media_type: LOCALIZE_MEDIA_TYPE,
    ad: {
      params: {
        language,
        subject_type: subjectType,
        subject: {
          id: subject.id,
          name: subject.name,
          headline: subject.headline ?? '',
          subtitle: subject.subtitle ?? '',
          cta_text: subject.cta_text ?? '',
        },
      },
    },
  };
  const res = await adApiRequest<LocalizeAnswer>('post', '/creatives/generate', body, signal);
  if (!res.ok) return res;
  const data = res.data;
  if (data.ok === false) return { ok: false, status: 200, error: str(data.error) || undefined };
  const sources = isRecord(data.sources) ? data.sources : {};
  return {
    ok: true,
    data: {
      language: str(data.language) || language,
      subject_id: subject.id,
      headline: str(data.headline),
      subtitle: str(data.subtitle),
      cta_text: str(data.cta_text),
      translated: LOCALIZED_FIELDS.filter((f) => sources[f] === 'translated'),
    },
  };
};

/** The brand website's languages as the Language picker offers them. */
export interface SiteLanguages {
  /** The website's languages, the default first, each once. */
  languages: string[];
  /** The website's default language ('' when unknown). */
  defaultLanguage: string;
  /** Whether the languages could be read (false: the picker is disabled and documents keep their language). */
  known: boolean;
}

/** How long the website-languages read may take before it counts as failed. */
const SITE_LANGUAGES_TIMEOUT_MS = 6000;

/** One read per tenant, shared by every composer; a failed read is dropped so the next caller tries again. */
const siteLanguageRequests = new Map<number, Promise<SiteLanguages | null>>();
/** The answered reads, so a composer opened after the first read starts with the languages (no loading flash). */
const siteLanguageAnswers = new Map<number, SiteLanguages>();

/**
 * The brand website's languages (`GET /creatives/tenant/{t}/landing-pages/legal`), read once per tenant. null when
 * the read failed (not cached: the next call reads again); `known: false` when the service could not tell them.
 */
export const fetchSiteLanguages = (tenantId: number): Promise<SiteLanguages | null> => {
  const cached = siteLanguageRequests.get(tenantId);
  if (cached) return cached;
  // The read resolves the site through several services: a stalled one must not hold a mint or a composer's
  // Generate, so it gives up after SITE_LANGUAGES_TIMEOUT_MS like a failed read (null, retried by the next caller).
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), SITE_LANGUAGES_TIMEOUT_MS);
  const request = fetchLandingLegal(tenantId, ctrl.signal)
    .then((res): SiteLanguages | null => {
      clearTimeout(timer);
      if (!res.ok) {
        siteLanguageRequests.delete(tenantId);
        return null;
      }
      const languages = siteLanguageList(res.data.languages, res.data.default_language);
      const answer: SiteLanguages = {
        languages,
        defaultLanguage: languages[0] ?? '',
        known: res.data.languages_known && languages.length > 0,
      };
      siteLanguageAnswers.set(tenantId, answer);
      return answer;
    })
    .catch((): null => {
      clearTimeout(timer);
      siteLanguageRequests.delete(tenantId);
      return null;
    });
  siteLanguageRequests.set(tenantId, request);
  return request;
};

/** The website's languages when they have already been read for this tenant (no request). */
export const cachedSiteLanguages = (tenantId: number): SiteLanguages | undefined => siteLanguageAnswers.get(tenantId);

/** The language a new asset starts in: the website's default when its languages could be read, else '' (the asset
 *  then renders as before until a later fill). Awaits the shared read. */
export const siteDefaultLanguage = async (tenantId: number): Promise<string> => {
  const site = await fetchSiteLanguages(tenantId);
  return site?.known ? site.defaultLanguage : '';
};
